import { useRef, useState } from "react";
import { useEngine } from "../../state/engine.tsx";
import { Panel } from "../../components/Panel.tsx";

export function SourcesPage() {
  const { sources, addOntology, addEndpoint, removeSource, llmEnabled, setAnthropicKey, settings } = useEngine();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [epLabel, setEpLabel] = useState("");
  const [epUrl, setEpUrl] = useState("");
  const [keyInput, setKeyInput] = useState(settings.anthropicKey ?? "");

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      await addOntology(file);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-5">
      <section className="pt-2">
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: "var(--nl2s-text)" }}>
          Sources & settings
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--nl2s-text-muted)" }}>
          Every linked term carries which source can supply it. Upload an ontology to query it in plain
          language; add an endpoint to run SPARQL (including federated <code>SERVICE</code> queries).
        </p>
      </section>

      <Panel title="Registered sources">
        <ul className="space-y-2">
          {sources.map((s) => (
            <li
              key={s.id}
              className="flex items-start gap-3 rounded-lg border p-3"
              style={{ borderColor: "var(--nl2s-border)" }}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold" style={{ color: "var(--nl2s-text)" }}>{s.label}</span>
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: "var(--nl2s-surface-raised)", color: "var(--nl2s-text-muted)" }}>
                    {s.kind}
                  </span>
                  {s.isDefault ? (
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: "var(--nl2s-accent-soft)", color: "var(--nl2s-accent)" }}>
                      default
                    </span>
                  ) : null}
                </div>
                <div className="mt-0.5 text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
                  {s.description ?? s.endpoint}
                </div>
              </div>
              {s.id !== "wikidata" ? (
                <button
                  type="button"
                  onClick={() => removeSource(s.id)}
                  className="rounded-md px-2 py-1 text-xs"
                  style={{ color: "var(--nl2s-danger)", background: "var(--nl2s-surface-raised)" }}
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="Upload an ontology" subtitle="Turtle / N-Triples / TriG / N-Quads — parsed and queried in your browser">
        <input
          ref={fileRef}
          type="file"
          accept=".ttl,.nt,.trig,.nq,.rdf,.owl,.n3,text/turtle"
          onChange={(e) => onFile(e.target.files?.[0])}
          className="text-sm"
          style={{ color: "var(--nl2s-text-muted)" }}
        />
        {uploading ? (
          <div className="mt-2 text-xs" style={{ color: "var(--nl2s-text-muted)" }}>Parsing…</div>
        ) : null}
        {uploadError ? (
          <div className="mt-2 rounded-md p-2 text-xs" style={{ background: "var(--nl2s-accent-soft)", color: "var(--nl2s-danger)" }}>
            {uploadError}
          </div>
        ) : null}
        <p className="mt-3 text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
          After uploading, pick it (or “Auto”) in the source selector on the Ask page. Labels come from
          <code> rdfs:label</code>/<code>skos:prefLabel</code>; entity &amp; relation questions work today.
        </p>
      </Panel>

      <Panel title="Add a SPARQL endpoint" subtitle="A query target for the editor — e.g. DBpedia, a Fuseki/Oxigraph server">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
            Label
            <input value={epLabel} onChange={(e) => setEpLabel(e.target.value)} placeholder="DBpedia" className="mt-1 block rounded-md border px-2 py-1 text-sm" style={{ borderColor: "var(--nl2s-border-strong)", background: "var(--nl2s-surface)", color: "var(--nl2s-text)" }} />
          </label>
          <label className="flex-1 text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
            Endpoint URL
            <input value={epUrl} onChange={(e) => setEpUrl(e.target.value)} placeholder="https://dbpedia.org/sparql" className="mt-1 block w-full rounded-md border px-2 py-1 text-sm" style={{ borderColor: "var(--nl2s-border-strong)", background: "var(--nl2s-surface)", color: "var(--nl2s-text)" }} />
          </label>
          <button
            type="button"
            disabled={!/^https?:\/\//.test(epUrl)}
            onClick={() => {
              addEndpoint(epLabel, epUrl);
              setEpLabel("");
              setEpUrl("");
            }}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
            style={{ background: "var(--nl2s-accent)", color: "var(--nl2s-accent-contrast)" }}
          >
            Add
          </button>
        </div>
        <p className="mt-3 text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
          Endpoints are query targets for the SPARQL editor (natural-language linking needs a per-source
          lookup service, so it stays on Wikidata + uploaded ontologies). Cross-origin endpoints may need
          the optional server’s <code>/proxy</code>.
        </p>
      </Panel>

      <Panel title="Optional LLM parser (bring your own key)" subtitle="Used only when no template matches — it emits the same structured match, never raw SPARQL">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
            Anthropic API key
            <input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="sk-ant-…" className="mt-1 block w-full rounded-md border px-2 py-1 text-sm" style={{ borderColor: "var(--nl2s-border-strong)", background: "var(--nl2s-surface)", color: "var(--nl2s-text)" }} />
          </label>
          <button type="button" onClick={() => setAnthropicKey(keyInput.trim() || undefined)} className="rounded-lg px-3 py-1.5 text-sm font-semibold" style={{ background: "var(--nl2s-accent)", color: "var(--nl2s-accent-contrast)" }}>
            {keyInput.trim() ? "Save key" : "Save"}
          </button>
          {llmEnabled ? (
            <button type="button" onClick={() => { setKeyInput(""); void setAnthropicKey(undefined); }} className="rounded-lg px-3 py-1.5 text-sm" style={{ background: "var(--nl2s-surface-raised)", color: "var(--nl2s-text-muted)" }}>
              Disable
            </button>
          ) : null}
        </div>
        <div className="mt-2 text-xs" style={{ color: llmEnabled ? "var(--nl2s-ok)" : "var(--nl2s-text-muted)" }}>
          {llmEnabled ? "● LLM fallback enabled (Claude Haiku)." : "○ Deterministic only."}
        </div>
        <p className="mt-2 text-xs" style={{ color: "var(--nl2s-warn)" }}>
          ⚠ The key is stored in your browser and sent directly to Anthropic from this page. Use a
          personal key; a server-held key is the production path.
        </p>
      </Panel>
    </div>
  );
}
